import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useResendEmailLoginCode } from '../../../hooks/api/auth/mfa/useResendEmailLoginCode';
import Button from '../../generic/Button/Button';
import { BUTTON_TYPE } from '../../generic/Button/button-constants';
import VerificationCodeInput from '../../generic/Forms/VerificationCodeInput';
import { useAuthContext } from '../context/AuthContext';

/**
 * Email MFA verification form.
 * Displays code input and resend button.
 */
const EmailMfaVerification: VoidFunctionComponent = () => {
  const { mfaCode, submitButton, statusMethods, status } = useAuthContext();
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendMutation = useResendEmailLoginCode();

  const handleResend = async () => {
    if (resendCooldown > 0) {
      return;
    }

    statusMethods.onPending();
    try {
      await resendMutation.mutateAsync();
      statusMethods.onSuccess();
      // Start 60-second cooldown
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      statusMethods.onError(error);
    }
  };

  return (
    <>
      <p className="text-center margin-bottom-md">
        Enter the 6-digit verification code sent to your email.
      </p>
      <VerificationCodeInput
        value={mfaCode.value}
        onChange={mfaCode.onChange}
        onComplete={submitButton.onSubmit}
        disabled={status.isLoading}
      />
      <div className="text-center margin-top-md">
        <Button
          type={BUTTON_TYPE.ANCHOR}
          onClick={handleResend}
          isDisabled={resendCooldown > 0 || resendMutation.isPending}
        >
          {resendCooldown > 0
            ? `Resend code (${resendCooldown}s)`
            : 'Resend code'}
        </Button>
      </div>
    </>
  );
};

export default EmailMfaVerification;
